import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { Grid } from '@mui/material';

import { Errors, validateYup } from 'services/forms/validation';
import { useHandleTextFieldChange } from 'services/forms';
import { Modal } from 'ui/components/Modal/Modal';
import { TextField } from 'ui/components/TextField/TextField';
import { getPaymentTerms, PaymentTerm } from 'services/paymentTerms';

import {
  createCustomer,
  Customer,
  fetchCustomers,
  initialCustomer,
} from 'services/customers';
import { Currency } from 'services/currencies';

import { Autocomplete } from '../Autocomplete';
import { NewCustomerModalProps } from './types';
import { CurrencyAutocomplete } from '../CurrencyAutocomplete';
import { getSettingsCompany } from 'services/settings/company';

const yupSchema = yup.object().shape({
  name: yup.string(),
  paymentTermId: yup.number(),
});

const NewCustomerModal: React.FC<NewCustomerModalProps> = (props) => {
  const { show, newCustomerName, onClose, onSave } = props;
  const useMultiCurrencyEnabled =
    useSelector(getSettingsCompany).useMultiCurrency;
  const dispatch = useDispatch();

  const { items: paymentTerms } = useSelector(getPaymentTerms);

  const [formCustomer, setFormCustomer] = useState<Customer>(initialCustomer);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const selectedPaymentTerm = useMemo(() => {
    return (
      paymentTerms.find((p) => p.id === formCustomer.paymentTermId) || null
    );
  }, [paymentTerms, formCustomer]);

  useEffect(() => {
    setFormCustomer({
      ...initialCustomer,
      name: newCustomerName,
    });
    setErrors({});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handlePaymentTermChange = useCallback(
    (e: any, v: PaymentTerm | null) => {
      setFormCustomer((old) => ({ ...old, paymentTermId: v ? v.id : null }));
    },
    [setFormCustomer]
  );

  const handleCurrencyChange = useCallback(
    (currency: Currency | null) => {
      setFormCustomer((old) => ({
        ...old,
        currencyId: currency ? currency.id : null,
        currency: currency,
      }));
    },
    [setFormCustomer]
  );

  const handleApplyModal = async () => {
    if (!validateYup(formCustomer, yupSchema, setErrors)) {
      return;
    }

    setIsLoading(true);

    try {
      const newCustomer = await createCustomer(formCustomer);
      // Lint skip to be removed
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      dispatch(fetchCustomers());
      onSave(newCustomer);
    } catch {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setFormCustomer(initialCustomer);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormCustomer,
    formCustomer
  );

  return (
    <Modal
      open={show}
      title="New Customer"
      onCancelClicked={onClose}
      onApplyClicked={handleApplyModal}
      isLoadingContent={isLoading}
      dataQa="customer"
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            className="redesign"
            variant="standard"
            name="name"
            label="Name"
            value={formCustomer.name}
            onChange={handleTextFieldChange}
            required
            error={!!errors.name}
            dataQa="customer-name"
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            onChange={handlePaymentTermChange}
            value={selectedPaymentTerm}
            label="Payment Terms"
            options={paymentTerms}
            getOptionLabel={(paymentTerm: PaymentTerm) =>
              paymentTerm.name || ''
            }
            error={!!errors.paymentTermId}
            required
            dataQa="customer-payment-terms"
          />
        </Grid>
        {useMultiCurrencyEnabled ? (
          <Grid item xs={6}>
            <CurrencyAutocomplete
              label="Currency"
              placeholder="Select currency"
              required
              value={formCustomer.currency}
              onChange={handleCurrencyChange}
            />
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
    </Modal>
  );
};

export default memo(NewCustomerModal);
